import { queryClient, type RouterInputs, trpc, trpcOptions, type trpcQueryUtils } from '@client/trpc/client';
import {
    ASIC_PRODUCTS_FULL_TREE_INPUT,
    DETAIL_LOOKUP_PREFETCH_OPTIONS,
    DETAIL_RECORD_PREFETCH_OPTIONS,
    fieldConfigurationViewInput,
} from '@client/trpc/query-options';
import type { QueryKey } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';

/**
 * Input shared by the manage tab's mount-time config lookups and the route-level
 * prefetch. The two must serialise to the same query key, or the prefetch warms
 * nothing — import this constant instead of re-typing the literal.
 */
export const INCIDENT_CONFIG_LIST_INPUT = {
    orderBy: { sortOrder: 'asc' },
    where: { isDisabled: false },
} as const;

/**
 * First page of the manage tab's Obligations card. The card's initial table input and the
 * route-level prefetch must serialise to the same query key, for the same reason as
 * `INCIDENT_CONFIG_LIST_INPUT`; pagination then replaces it.
 */
export function incidentObligationsFirstPageInput(id: bigint) {
    return { id, limit: 5, offset: 0 } as const satisfies RouterInputs['incident']['listIncidentObligations'];
}

/**
 * Fire-and-forget warm-up for the incident details page. `getIncident` gates the
 * page's LCP element (the description rich text) and, transitively, every
 * manage-tab query — none of them can start until it resolves and the tab
 * mounts. Firing from `beforeLoad` overlaps the fetch with route-chunk parsing
 * and the auth-bootstrap render gate. Failures fall through to the render-time
 * `useQuery` retry path.
 *
 * Also warms the lookups the manage tab's eager cards render from — the INCIDENT
 * custom field configurations, the full ASIC product tree and the Obligations card's
 * first page and linked IDs — which otherwise leave in a second batch after the
 * record renders. Their observers use the short `DETAIL_LOOKUP_QUERY_OPTIONS` window:
 * - Only when the target shows the manage tab (`showsManageTab`). `beforeLoad` reruns
 *   for the retained details match on every in-page navigation, and the library and
 *   history tabs render neither lookup.
 * - Skipped per lookup while the manage tab is already mounted for this incident (the
 *   lookup and this incident's record are both observed; the Obligations card's pair goes
 *   by its linked IDs, observed whichever page its table shows), e.g. opening or closing
 *   the note and complaints drawers: the mounted observers own the lookups' freshness, and
 *   a refetch would resend the full ASIC tree. Another incident is unobserved and still
 *   prefetches, as its manage tab remounts behind a skeleton.
 * - A navigation prefetches with that window, so a lookup the observer would refetch
 *   on mount is refreshed here and the observer joins the in-flight request.
 * - A hover-intent preload keeps the 30 s detail-record window: the click reruns
 *   `beforeLoad` as a navigation, which refreshes anything older than the window.
 *   It skips the obligations entirely, so hovering list rows adds no requests; the
 *   click's navigation warms them in the `getIncident` batch.
 */
export function prefetchIncidentDetails(
    trpcUtils: typeof trpcQueryUtils,
    id: bigint,
    { preload, showsManageTab }: { preload: boolean; showsManageTab: boolean },
): void {
    // Read before any prefetch: a page observing this record is this incident's own.
    const isIncidentShown = isObserved(getQueryKey(trpc.incident.getIncident, { id }, 'query'));
    void trpcUtils.incident.getIncident.prefetch({ id }, DETAIL_RECORD_PREFETCH_OPTIONS);
    void trpcUtils.incident.getIncidentAiAnalysis.prefetch({ incidentId: id }, DETAIL_RECORD_PREFETCH_OPTIONS);
    void trpcUtils.incident.impactedParty.listIncidentImpactedParties.prefetch(
        INCIDENT_CONFIG_LIST_INPUT,
        DETAIL_RECORD_PREFETCH_OPTIONS,
    );
    void trpcUtils.incident.identificationMethod.listIncidentIdentificationMethods.prefetch(
        INCIDENT_CONFIG_LIST_INPUT,
        DETAIL_RECORD_PREFETCH_OPTIONS,
    );
    void trpcUtils.incident.remediationOption.listIncidentRemediationOptions.prefetch(
        INCIDENT_CONFIG_LIST_INPUT,
        DETAIL_RECORD_PREFETCH_OPTIONS,
    );
    void trpcUtils.incident.solutionImplemented.listIncidentSolutionsImplemented.prefetch(
        INCIDENT_CONFIG_LIST_INPUT,
        DETAIL_RECORD_PREFETCH_OPTIONS,
    );
    void trpcUtils.incident.rootCause.listIncidentRootCauses.prefetch(
        INCIDENT_CONFIG_LIST_INPUT,
        DETAIL_RECORD_PREFETCH_OPTIONS,
    );
    if (!showsManageTab) {
        return;
    }
    const lookupOptions = preload ? DETAIL_RECORD_PREFETCH_OPTIONS : DETAIL_LOOKUP_PREFETCH_OPTIONS;
    const isOwnedByMountedTab = (queryKey: QueryKey) => isIncidentShown && isObserved(queryKey);
    const fieldConfigurationsInput = fieldConfigurationViewInput('INCIDENT');
    if (
        !isOwnedByMountedTab(
            getQueryKey(trpc.fieldConfiguration.listFieldConfigurations, fieldConfigurationsInput, 'query'),
        )
    ) {
        void trpcUtils.fieldConfiguration.listFieldConfigurations.prefetch(fieldConfigurationsInput, lookupOptions);
    }
    if (!isOwnedByMountedTab(getQueryKey(trpc.asicProduct.listAsicProducts, ASIC_PRODUCTS_FULL_TREE_INPUT, 'query'))) {
        void trpcUtils.asicProduct.listAsicProducts.prefetch(ASIC_PRODUCTS_FULL_TREE_INPUT, lookupOptions);
    }
    if (preload) {
        return;
    }
    const obligationIds = trpcOptions.incident.getIncidentObligationIds.queryOptions(
        { id },
        DETAIL_LOOKUP_PREFETCH_OPTIONS,
    );
    // The card observes the linked IDs on every page, so they mark it mounted after paging
    // has moved its table observer off the first page.
    if (!isOwnedByMountedTab(obligationIds.queryKey)) {
        void queryClient.prefetchQuery(
            trpcOptions.incident.listIncidentObligations.queryOptions(
                incidentObligationsFirstPageInput(id),
                DETAIL_LOOKUP_PREFETCH_OPTIONS,
            ),
        );
        void queryClient.prefetchQuery(obligationIds);
    }
}

function isObserved(queryKey: QueryKey): boolean {
    return (queryClient.getQueryCache().find({ queryKey, exact: true })?.getObserversCount() ?? 0) > 0;
}
